import { Injectable } from '@angular/core';
import { Backend } from '@DataFriends-Group/ngx-core';
import { map, Observable } from 'rxjs';
import { HttpParams } from '@angular/common/http';

/**
 * Represents a request to create an order.
 *
 * This class is used to encapsulate the necessary information
 * required when creating a new order. It includes details about
 * the dealer, box, and contact information.
 */
export class CreateOrderRequest {
  constructor(
    public dealerId: number,
    public boxId: number,
    public phone: string,
  ) {}
}

/**
 * Represents a container with specific attributes and properties.
 *
 * @interface Box
 * @property {number} id - The unique identifier for the box.
 * @property {boolean} isCooled - Indicates whether the box has cooling functionality.
 * @property {string} label - The descriptive name or label assigned to the box.
 * @property {string} lockId - The identifier of the lock associated with the box.
 * @property {number} lockIdNumber - The numeric representation of the lock's identifier.
 * @property {string} lockIpAddress - The IP address of the lock for network communication.
 * @property {string} sensorId - The identifier of the sensor linked to the box.
 * @property {string} sensorIpAddress - The IP address of the sensor for network communication.
 * @property {string} size - The physical dimensions or size category of the box.
 */
export interface Box {
  id: number;
  isCooled: boolean;
  label: string;
  lockId: string;
  lockIdNumber: number;
  lockIpAddress: string;
  sensorId: string;
  sensorIpAddress: string;
  size: string;
}

/**
 * Represents a customer order containing information about the order's details, dealer, and status.
 *
 * @interface CustomerOrder
 *
 * @property {Box} box - Represents the Box object.
 * @property {number} boxId - A unique identifier for the box associated with the order.
 * @property {string} createdAt - The timestamp when the order was created, in ISO 8601 format.
 * @property {number} dealerId - The unique identifier of the dealer associated with the order.
 * @property {number} id - The unique identifier for the customer order.
 * @property {string} phone - The contact phone number associated with the order.
 * @property {string} pickupPin - A security PIN required for order pickup.
 * @property {string} state - The current state or status of the order, e.g., "pending", "completed".
 */
export interface CustomerOrder {
  box: Box;
  boxId: number;
  createdAt: string;
  dealerId: number;
  id: number;
  phone: string;
  pickupPin: string;
  state: string;
}

/**
 * Service responsible for handling customer order-related operations.
 * Provides methods to create, list, and cancel (storno) customer orders.
 * Extends the Backend class with a generic type for unknown response data
 * and CreateOrderRequest for request payloads.
 *
 * This service is decorated with `@Injectable` and is provided at the root level.
 */
@Injectable({
  providedIn: 'root',
})
export class CustomerOrderService extends Backend<unknown, CreateOrderRequest> {
  constructor() {
    super('customerorder');
  }

  /**
   * Sends a request to cancel or reverse an order identified by the given ID.
   *
   * @param {number} id - The unique identifier of the order to be reversed.
   * @return {Observable<boolean>} An observable that emits a boolean indicating
   * whether the operation was successful.
   */
  stornoOrder(id: number): Observable<boolean> {
    return this.post(`${this.apiUrl}${id}/storno`).pipe(
      map((result) => {
        return result.success;
      }),
    );
  }

  /**
   * Retrieves a list of customer orders for a specific dealer.
   *
   * @param {number | null} delaerId - The ID of the dealer. Pass `null` if the dealer ID is not available.
   * @param {boolean} isService - Whether the request is made in service mode.
   * @return {Observable<CustomerOrder[]>} An observable that emits an array of customer orders.
   */
  listOrder(delaerId: number | null, isService: boolean): Observable<CustomerOrder[]> {
    const params = new HttpParams().append('isService', isService.toString());

    return this.get(`${this.apiUrl}dealer/${delaerId ?? 0}`, params).pipe(
      map((result) => {
        return result.response;
      }),
    );
  }

  /**
   * Creates a new order by sending a request to the API.
   *
   * @param {CreateOrderRequest} request - The request object containing order details.
   * @return {Observable<boolean>} An Observable that emits a boolean value indicating whether the order creation was successful.
   */
  createOrder(request: CreateOrderRequest): Observable<boolean> {
    return this.post(this.apiUrl, request).pipe(map((result) => result.success));
  }

  /**
   * Processes an order request based on the provided pin.
   *
   * @param {string} pin - The pin code for identifying the order.
   * @return {Observable<string>} An observable that emits the server's response as a string
   * indicating success or an error message.
   */
  takeOrder(pin: string): Observable<string> {
    return this.post(`${this.apiUrl}take`, JSON.stringify(pin), false).pipe(
      map((result) => {
        return result.success ? result.response?.message : 'Nastala chyba';
      }),
    );
  }
}
